import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'emotion-theming';
import ReactGA from 'react-ga';
import {
  Wizard,
  Modal,
  Heading,
  Flex,
  Button,
  FieldGroup,
  RadioButton,
  Box,
  BannerHeader,
  rem,
  base,
  togatherTheme,
} from '@feast-it/pesto';
import { useMutation } from '@apollo/react-hooks';

import paths from '../../constants/paths';
import Layout from '../../containers/layout';
import LayoutSkeleton from '../../containers/layout/LayoutSkeleton';
import { getPages } from '../../containers/forms/enquiry';
import { getTags, formatTagsAsOptions } from '../../modules/tags';
import EnquiryContext from '../../context/enquiryForm';
import { getSupplier } from '../../modules/suppliers';
import { removeFalsy } from '../../modules/object';
import { trackEvent, tagManagerTrack } from '../../modules/analytics';
import { asArray } from '../../modules/helpers';
import { addPreferredSupplier } from '../../modules/eventRequests';
import {
  ENQUIRY_FORM_NEXT_CLICKED,
  ENQUIRY_FORM_BACK_CLICKED,
  ENQUIRY_FORM_ABANDONED,
  ENQUIRY_FORM_CLOSE_CLICKED,
  EVENT_REQUEST,
  CATERING_ABOUT_EVENT_EXPERIEMENT,
  SUBMITTED_REQUEST_ATTRIBUTION,
  ENQUIRY_FORM_ABANDONED_REASON,
} from '../../constants/analytics';

import Meta from '../../components/Meta';
import HideFreshChat from '../../components/HideFreshChat';
import { getTier1Verticals, getTier2Verticals } from '../../modules/verticals';
import { TIER_1_PAGE, TIER_2_PAGE } from '../../containers/forms/pages';
import { createUser } from '../../modules/user';
import { createEventWithRequestsPayload } from '../../modules/events';
import {
  CREATE_EVENT_WITH_REQUESTS,
  CREATE_PRIORITY_TAG,
  CREATE_CUSTOMER_REFERRAL,
} from '../../mutations';
import { captureError } from '../../modules/monitoring';
import { TEST_NAMES } from '../../constants/test-names';
import useTestTreatment from '../../hooks/testTreatment/testTreatment';
import { GCLID, ON_LOAD_URL } from '../../constants/session-storage-keys';
import { useBreakpoint } from '../../hooks/media';
import {
  useAnonymousIdToUserId,
  useTestTreatmentV2,
} from '../../hooks/testTreatmentV2';
import { useUserId } from '../../hooks/useUserId';

const Enquiry = ({
  tier1Verticals,
  bookingUrgencyTags,
  initialQuery: queryParams,
  supplier,
  isLoggedIn,
  isLoadingUser,
  user,
}) => {
  const isSupplier = user?.type === 'supplier';
  const isDesktop = useBreakpoint(base.breakpoints.m);

  const userId = useUserId(user.id);

  const getCateringTest = useTestTreatment(
    userId.id,
    TEST_NAMES.CATERING_ABOUT_EVENT_TEST
  );

  const onAbandonEnquiryTest = useTestTreatment(
    userId.id,
    TEST_NAMES.ABANDON_ENQUIRY
  );

  const onWelcomeEnquiryPageTest = useTestTreatment(
    userId.id,
    TEST_NAMES.REMOVE_ENQUIRY_WELCOME_PAGE
  );

  const multiVerticalPageTest = useTestTreatmentV2(
    userId.isAnonymousUser ? 'ANONYMOUS_ID' : 'USER_ID',
    userId.id,
    TEST_NAMES.MULTI_VERTICAL_PAGE_TEST
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { setAnonymousIdToUserId } = useAnonymousIdToUserId();

  const supplierHasMultipleTier2s = supplier?.tier2?.length > 1;
  const [exitModalIsOpen, setExitModalIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [exitPage, setExitPage] = useState(null);
  const [pages, setPages] = useState([]);
  const [tier1, setTier1] = useState('');
  const [tier2, setTier2] = useState('');
  const [eventType, setEventType] = useState('');
  const [tier2Verticals, setTier2Verticals] = useState([]);
  const [selectedTag, setSelectedTag] = useState(false);
  const [createEventMutation] = useMutation(CREATE_EVENT_WITH_REQUESTS);
  const [createPriorityTagMutation] = useMutation(CREATE_PRIORITY_TAG);
  const [createCustomerReferralLinksMutation] = useMutation(
    CREATE_CUSTOMER_REFERRAL
  );
  const [showCateringExperiment, setShowCateringExperiment] = useState(false);
  const [exitReason, setExitReason] = useState('');

  const shouldIncludePrirotityTagPage = tier1 === 'food' && selectedTag;
  const isCateringExperiment = tier2 === 'caterers' && eventType === 'Wedding';
  const shouldShowAbandonReason = onAbandonEnquiryTest.testTreatment === 'on';
  const removeEnquiryWelcomePage =
    onWelcomeEnquiryPageTest.testTreatment === 'on';
  const showMultiVerticalPageTest =
    multiVerticalPageTest.testTreatment === 'on';
  const initialQuery = showMultiVerticalPageTest ? {} : queryParams;

  useEffect(() => {
    const showCateringTest = isCateringExperiment;
    getCateringTest.testTreatment === 'on';

    setShowCateringExperiment(showCateringTest);
  }, [tier2, eventType, getCateringTest.testTreatment, isCateringExperiment]);

  useEffect(() => {
    if (!isLoadingUser) {
      setPages(
        getPages(isLoggedIn, undefined, undefined, undefined, {
          shouldIncludePrirotityTagPage,
          removeEnquiryWelcomePage,
          showMultiVerticalPageTest,
        })
      );
    }
  }, [
    isLoggedIn,
    isLoadingUser,
    shouldIncludePrirotityTagPage,
    removeEnquiryWelcomePage,
    showMultiVerticalPageTest,
  ]);

  useEffect(() => {
    setPages(
      getPages(isLoggedIn, tier1, tier2, eventType, {
        shouldIncludePrirotityTagPage,
        removeEnquiryWelcomePage,
        showMultiVerticalPageTest,
      })
    );
  }, [
    eventType,
    isLoggedIn,
    selectedTag,
    shouldIncludePrirotityTagPage,
    tier1,
    tier2,
    removeEnquiryWelcomePage,
    showMultiVerticalPageTest,
  ]);

  const shouldSkipPage = stage =>
    supplier &&
    (stage === TIER_1_PAGE.stage ||
      (!supplierHasMultipleTier2s && stage == TIER_2_PAGE.stage));

  const onFormNext = (nextPage, values) => {
    if (
      values.request?.category_tier1 &&
      values.request?.category_tier1 !== tier1
    ) {
      setTier1(values.request.category_tier1);

      if (supplier) {
        setTier2Verticals(
          supplier.tier2.map(vertical => ({
            label: vertical.name,
            value: vertical.sysName,
          }))
        );
      } else {
        setTier2Verticals([]);

        getTier2Verticals(values.request.category_tier1).then(verticals => {
          setTier2Verticals(
            verticals.map(vertical => ({
              label: vertical.name,
              value: vertical.sysName,
            }))
          );
        });
      }
    }

    if (values.event?.type && values.event.type !== eventType) {
      setEventType(values.event?.type);
    }

    if (
      values.request?.category_tier2 &&
      values.request?.category_tier2 !== tier2
    ) {
      setTier2(values.request.category_tier2);

      setPages(
        getPages(
          isLoggedIn,
          values.request.category_tier1,
          values.request.category_tier2,
          values.event?.type,
          {
            shouldIncludePrirotityTagPage,
            removeEnquiryWelcomePage,
          }
        )
      );
    }

    const trackingProperties = {
      stage: pages[nextPage].stage,
    };

    if (values.request?.category_tier1) {
      trackingProperties.vertical = values.request.category_tier1;
    }

    if (values.event?.locationDecided) {
      trackingProperties.locationDecided = values.event?.locationDecided;
    }

    if (values.additional?.requests) {
      trackingProperties.additionalRequests = values.additional.requests;
    }

    if (values.request?.category_tier2) {
      trackingProperties.tier2 = values.request.category_tier2;
    }

    if (
      pages[nextPage].stage === 'vertical-stage-DESCRIPTION' &&
      isCateringExperiment
    ) {
      trackEvent(CATERING_ABOUT_EVENT_EXPERIEMENT, {
        treatment: getCateringTest.testTreatment,
      });
    }

    trackEvent(ENQUIRY_FORM_NEXT_CLICKED, trackingProperties);
  };

  const onFormBack = (prevPage, values) => {
    const trackingProperties = {
      stage: pages[prevPage].stage,
    };

    if (values.request?.category_tier1) {
      trackingProperties.vertical = values.request.category_tier1;
    }

    if (values.event?.locationDecided) {
      trackingProperties.locationDecided = values.event?.locationDecided;
    }

    trackEvent(ENQUIRY_FORM_BACK_CLICKED, trackingProperties);
  };

  const handleLeave = useCallback(e => {
    e.preventDefault();
    e.returnValue = '';
  }, []);

  const onExitForm = useCallback(() => {
    if (shouldShowAbandonReason) {
      trackEvent(ENQUIRY_FORM_ABANDONED_REASON, {
        stage: pages[exitPage].stage,
        reason: exitReason,
      });
    }

    trackEvent(ENQUIRY_FORM_ABANDONED, {
      stage: pages[exitPage].stage,
    });

    window.location.href = paths.HOME;
  }, [pages, exitPage, exitReason, shouldShowAbandonReason]);

  const triggerExitModal = useCallback(
    page => {
      setExitModalIsOpen(true);
      setExitPage(page);
      window.removeEventListener('beforeunload', handleLeave);

      trackEvent(ENQUIRY_FORM_CLOSE_CLICKED, {
        stage: pages[page].stage,
      });
    },
    [pages, handleLeave]
  );

  const closeExitModal = useCallback(() => {
    setExitModalIsOpen(false);
    setExitPage(null);
    window.addEventListener('beforeunload', handleLeave);
  }, [handleLeave]);

  useEffect(() => {
    window.addEventListener('beforeunload', handleLeave);

    return () => window.removeEventListener('beforeunload', handleLeave);
  }, [handleLeave]);

  const ERROR_MESSAGES = {
    218004: 'This email is already in use, login to your account to create new events.',
    198000: 'This phone number is not valid. Please enter a valid phone number',
    default:
      "We've encountered an error when creating your event, please retry or contact us for support.",
  };

  const handleSubmit = async values => {
    if (isSubmitting) {
      return;
    } else {
      setIsSubmitting(true);
    }

    window.removeEventListener('beforeunload', handleLeave);

    const requestConversion = {
      category: EVENT_REQUEST,
      action: 'Form Submission',
      label: 'Main Form',
      value: 1,
    };
    ReactGA.event(requestConversion);
    tagManagerTrack(EVENT_REQUEST, requestConversion);

    const getEventRequestPayload = submittedValues => {
      const tags = [...submittedValues.request.tags];

      if (submittedValues.request.category_tier1 === 'venues') {
        tags.push(...submittedValues.venue.tags);
      }

      if (
        submittedValues.request.category_tier1 === 'venues' &&
        submittedValues.venue.layout !== 'Not sure'
      ) {
        tags.push(submittedValues.venue.layout);
      }

      if (submittedValues.request.coverageHours) {
        tags.push(submittedValues.request.coverageHours);
      }

      const requestBody = removeFalsy({
        gclid: sessionStorage.getItem(GCLID) ?? null,
        completed: true,
        tag_uuids: tags,
        incomplete_verticals: values.additional?.requests ?? [],
        ...submittedValues.request,
        additional_info: removeFalsy({
          ...submittedValues.request.additional_info,
        }),
      });

      return requestBody;
    };

    const createEventWithRequests = async () => {
      const eventPayload = await createEventWithRequestsPayload({
        event: {
          ...values.event,
          event_areas: values.venue.location_areas,
          supplier_referral: initialQuery.referralId,
          customer_referral: initialQuery.customerReferralId,
          type:
            values.request.category_tier1 === 'virtual'
              ? 'Virtual Event'
              : values.event.type,
          freshsalesRequestInfo: {
            requestCount: values.additional.requests.length + 1,
            budget: values.request.budget ?? undefined,
          },
        },
        request: getEventRequestPayload(values),
      });

      const eventWithRequests = await createEventMutation({
        variables: eventPayload,
      });

      if (!eventWithRequests?.data?.error) {
        const createdRequest =
          eventWithRequests?.data?.createEventWithRequests?.data;

        trackEvent(SUBMITTED_REQUEST_ATTRIBUTION, {
          onloadUrl: sessionStorage.getItem(ON_LOAD_URL) ?? null,
          gclid: eventPayload.request.gclid ?? false,
          createRequestId: createdRequest?.requestIds?.uuid,
        });
      }

      return eventWithRequests;
    };

    try {
      if (!isLoggedIn) {
        try {
          const userPayload = {
            ...values.user,
            email: values.user.email.toLowerCase(),
          };
          const createdUser = await createUser(userPayload);

          if (userId && userId.isAnonymousUser && showMultiVerticalPageTest) {
            await setAnonymousIdToUserId({
              variables: {
                userId: createdUser.user,
                anonymousId: userId.id,
              },
            });
          }

          await createCustomerReferralLinksMutation({
            variables: {
              customerId: createdUser.user,
            },
          });
        } catch (userCreationError) {
          setError(
            ERROR_MESSAGES[userCreationError.code] || ERROR_MESSAGES.default
          );
          captureError(new Error(userCreationError.message));

          setIsSubmitting(false);

          return;
        }
      }

      const createdEvent = (await createEventWithRequests())?.data
        ?.createEventWithRequests;

      if (createdEvent.error) {
        setError(ERROR_MESSAGES.default);
        captureError(new Error(createdEvent.error.message));

        setIsSubmitting(false);
      } else {
        if (supplier) {
          try {
            await addPreferredSupplier(
              createdEvent.data?.requestIds?.uuid,
              supplier.uuid
            );
          } catch (preferredSupplierError) {
            captureError(preferredSupplierError);
          }
        }

        if (values?.request?.priorityTags.length) {
          await createPriorityTagMutation({
            variables: {
              eventRequestId: createdEvent.data?.requestIds?.uuid,
              tagId: values.request.priorityTags[0],
            },
          });
        }

        if (values?.additional?.requests?.length) {
          window.location.href = `${paths.CUSTOMER_EVENT(
            createdEvent.data?.eventIds?.id
          )}?action=incomplete-request`;
        } else {
          window.location.href = paths.CUSTOMER_EVENT(
            createdEvent.data?.eventIds?.id
          );
        }
      }
    } catch (eventCreationError) {
      setError(ERROR_MESSAGES.default);
      captureError(eventCreationError);

      setIsSubmitting(false);
    }
  };

  const onAbandonOptions = [
    'I don’t have time to fill it out right now',
    'I’m not ready to enquire yet',
    'I’m in the wrong place',
  ];

  if (!pages.length) {
    return <LayoutSkeleton />;
  }

  return (
    <Layout showFooter={false} showHeader={false}>
      <Meta
        path={paths.ENQUIRY_FORM}
        title="Get A Quote With Togather"
        subtitle="Make A Booking Enquiry"
      />

      <ThemeProvider theme={togatherTheme}>
        <HideFreshChat />

        <EnquiryContext.Provider
          value={{
            tier1Verticals,
            tier2Verticals,
            supplier,
            showCateringExperiment,
            tags: { bookingUrgencyTags },
            error,
          }}
        >
          {isSupplier && (
            <BannerHeader
              width="100%"
              px={{
                _: 'gutter._',
                s: 'gutter.s',
              }}
              position="fixed"
              messagePosition={{ _: 'flex-start', m: 'center' }}
              lineHeight="headingSmall"
              py={2}
              icon={isDesktop && 'InfoOutline'}
              message="This is the customer enquiry form. Feel free to have a look around but you won't be able to submit anything whilst logged in as a supplier."
            />
          )}

          <Wizard
            withHeader
            position="fixed"
            top="0"
            right="0"
            bottom="0"
            left="0"
            pt={isSupplier ? { _: rem(112), m: 9 } : 0}
            px={{ _: 'gutter._', s: 'gutter.s', m: 'gutter.m' }}
            initialValues={{
              request: {
                category_tier1:
                  initialQuery?.tier1 || supplier?.categoryTier1 || '',
                category_tier2:
                  initialQuery?.tier2 ||
                  (supplier && !supplierHasMultipleTier2s)
                    ? supplier?.tier2[0].sysName
                    : '',
                tags: [],
                priorityTags: [],
                filterTags: initialQuery?.filterTags || [],
                budget: initialQuery?.budget || '',
                item_quantity: initialQuery?.quantity || '',
                description: initialQuery?.description || '',
                coverageHours: initialQuery?.coverageHours || '',
                additional_info: {
                  courses: [],
                  children: '',
                  waiters: '',
                  crockery: '',
                  supplier_setup: '',
                  bar_type: '',
                  power: '',
                  water: '',
                },
                budget_flexible: false,
              },
              additional: {
                requests: [],
              },
              event: {
                date: initialQuery?.date || '',
                date_flexible: initialQuery?.date_flexible || false,
                type: initialQuery?.type || '',
                location_line_1: initialQuery?.location_line_1 || '',
                location_line_2: initialQuery?.location_line_2 || '',
                location_city: initialQuery?.location_city || '',
                postcode: initialQuery?.postcode || '',
                urgency: initialQuery?.urgency || '',
                locationAutocomplete: { value: '', label: '' },
                manualLocation: false,
                tags: [],
              },
              user: {
                firstName: user?.firstName || '',
                lastName: user?.lastName || '',
                phone: user?.phone || '',
                email: user?.email || '',
                role: user?.role || '',
                password: '',
                confirmPassword: '',
                terms: false,
              },
              venue: {
                location: '',
                location_areas: [],
                layout: '',
                tags: [],
              },
            }}
            onSubmit={handleSubmit}
            isDisabled={isSupplier || isSubmitting}
            onExit={triggerExitModal}
            onFormBack={onFormBack}
            onFormNext={onFormNext}
            shouldSkipPage={shouldSkipPage}
          >
            {pages.map((page, index) => {
              const PageComponent = page.component;

              return (
                <Wizard.Page
                  key={`enquiry-form-page-${index}`}
                  validationSchema={page.schema}
                  hideButtons={page.hideButtons}
                  stage={page.stage}
                >
                  <PageComponent
                    {...page?.props}
                    isLoadingUser={isLoadingUser}
                    isLoggedIn={isLoggedIn}
                    user={user}
                    showPriorityTagPage={setSelectedTag}
                  />
                </Wizard.Page>
              );
            })}
          </Wizard>

          <Modal
            open={exitModalIsOpen}
            handleClose={closeExitModal}
            hideCloseButton
            width={shouldShowAbandonReason ? 'auto' : '100%'}
          >
            {shouldShowAbandonReason ? (
              <Box>
                <Heading
                  as="h2"
                  color="blueDark"
                  variant="headingMedium"
                  mb={1}
                  fontSize={24}
                >
                  Want to leave the form?
                </Heading>
                <Heading
                  as="h3"
                  color="blueDark"
                  variant="headingSmall"
                  fontFamily="body"
                  mb={4}
                  fontSize={16}
                >
                  Let us know why you’re leaving
                </Heading>
                <FieldGroup
                  name="supplierType"
                  value={exitReason}
                  onChange={newValue => setExitReason(newValue)}
                  mb={4}
                >
                  {onAbandonOptions.map((option, index) => (
                    <RadioButton
                      key={index}
                      label={option}
                      value={option}
                      mr={6}
                      mb={3}
                      fontFamily="walsheim,sans-serif;"
                      color="blueDark"
                    />
                  ))}
                </FieldGroup>

                <Flex alignItems="center" flexDirection="column">
                  <Button width="100%" onClick={onExitForm}>
                    Yes, leave the form
                  </Button>
                  <Button
                    width="100%"
                    mt={3}
                    onClick={closeExitModal}
                    variant="darkOutline"
                  >
                    No, continue with the form
                  </Button>
                </Flex>
              </Box>
            ) : (
              <>
                <Heading
                  as="h3"
                  textAlign="center"
                  color="blueDark"
                  variant="headingSmall"
                  mb={4}
                >
                  Are you sure you want to leave? <br /> Your answers will be
                  lost.
                </Heading>
                <Flex alignItems="center" flexDirection="column">
                  <Button mb={3} onClick={closeExitModal}>
                    Take me back to the form!
                  </Button>
                  <Button variant="darkOutline" onClick={onExitForm}>
                    Yes, exit
                  </Button>
                </Flex>
              </>
            )}
          </Modal>
        </EnquiryContext.Provider>
      </ThemeProvider>
    </Layout>
  );
};

Enquiry.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
  isLoadingUser: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  tier1Verticals: PropTypes.arrayOf(PropTypes.object),
  bookingUrgencyTags: PropTypes.arrayOf(PropTypes.object),
  cuisineTags: PropTypes.arrayOf(PropTypes.object),
  additionalTags: PropTypes.arrayOf(PropTypes.object),
  supplier: PropTypes.shape({
    uuid: PropTypes.string,
    name: PropTypes.string,
    tier2: PropTypes.array,
    categoryTier1: PropTypes.string,
  }),
  initialQuery: PropTypes.shape({
    urgency: PropTypes.string,
    start_time: PropTypes.string,
    end_time: PropTypes.string,
    filterTags: PropTypes.array,
    location_line_1: PropTypes.string,
    location_line_2: PropTypes.string,
    location_city: PropTypes.string,
    date: PropTypes.string,
    tier1: PropTypes.string,
    tier2: PropTypes.array,
    type: PropTypes.string,
    line1: PropTypes.string,
    line2: PropTypes.string,
    city: PropTypes.string,
    postcode: PropTypes.string,
    bookingDate: PropTypes.string,
    tags: PropTypes.array,
    guests: PropTypes.number,
    budget: PropTypes.number,
    quantity: PropTypes.number,
    coverageHours: PropTypes.number,
    perHead: PropTypes.number,
    dietaryNotes: PropTypes.string,
    description: PropTypes.string,
    eventDate: PropTypes.string,
    referralId: PropTypes.string,
    customerReferralId: PropTypes.string,
    date_flexible: PropTypes.bool,
  }),
};

export const getServerSideProps = async context => {
  const { bookingUrgencyTags } = await getTags();
  const { query } = context;

  const tier1Verticals = await getTier1Verticals();

  let supplier = null;

  if (query?.supplier) {
    try {
      const { data } = await getSupplier(query.supplier);

      supplier = data;
    } catch (err) {
      // do nothing
    }

    delete query.supplier;
  }

  const tags = asArray(query?.['tags[]']);

  const initialQuery = removeFalsy({
    ...query,
    filterTags: tags,
  });

  return {
    props: {
      bookingUrgencyTags: formatTagsAsOptions(bookingUrgencyTags) || [],
      tier1Verticals,
      initialQuery,
      supplier,
    },
  };
};

export default Enquiry;
